import { memo, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Modal, Pressable, ScrollView, Linking, useWindowDimensions, StyleSheet as RNStyleSheet } from "react-native";
import Animated, { Easing, useAnimatedStyle, useSharedValue, withSpring, withTiming } from "react-native-reanimated";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import Logo from "@src/assets/svgs/logo-variant-red.svg";
import type { CameraSiteGroup } from "@src/features/cameras/utils/camera-group";
import { SiteCard } from "../SiteCard/index.native";
import { useTenant } from "@src/features/tenants/TenantProvider";
import { getTenantDisplayName } from "@src/features/tenants/tenant-utils";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { AppMotion } from "@src/constants/motion";

const DRAWER_BORDER_WIDTH = 1;

/** Extra surface past the left edge so spring overshoot does not reveal the screen behind. */
const DRAWER_LEFT_OVERSHOOT_BLEED = 48;

interface AppDrawerProps {
  visible: boolean;
  onClose: () => void;
  selectedSiteId: string | null;
  onSiteSelect: (siteId: string | null) => void;
  siteGroups: CameraSiteGroup[];
}

export const AppDrawer = memo(({ visible, onClose, selectedSiteId, onSiteSelect, siteGroups }: AppDrawerProps) => {
  const [modalPresented, setModalPresented] = useState(visible);

  const translateX = useSharedValue(0);
  const backdropOpacity = useSharedValue(0);

  useLayoutEffect(() => {
    if (visible) {
      setModalPresented(true);
    }
  }, [visible]);

  const { width: windowWidth } = useWindowDimensions();
  const drawerWidth = windowWidth * 0.85;

  useLayoutEffect(() => {
    if (!modalPresented) return;

    if (visible) {
      translateX.value = -drawerWidth;
      backdropOpacity.value = 0;
      translateX.value = withSpring(0, AppMotion.spring.subtle);
      backdropOpacity.value = withTiming(1, {
        duration: AppMotion.timing.fast,
        easing: Easing.out(Easing.cubic),
      });
    } else {
      const linearClose = {
        duration: AppMotion.timing.fast,
        easing: Easing.linear,
      };
      translateX.value = withTiming(-drawerWidth, linearClose);
      backdropOpacity.value = withTiming(0, linearClose);
    }
  }, [visible, modalPresented, drawerWidth, translateX, backdropOpacity]);

  useEffect(() => {
    if (visible || !modalPresented) return;
    const id = setTimeout(() => {
      setModalPresented(false);
    }, AppMotion.timing.fast);
    return () => clearTimeout(id);
  }, [visible, modalPresented]);

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value * 0.5,
  }));

  const drawerAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const { theme } = useUnistyles();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { selectedTenant, tenants } = useTenant();
  const { contactUrl } = useRegionConfig();

  const handleSettings = useCallback(() => {
    onClose();
    setTimeout(() => {
      router.push("/settings");
    }, 100);
  }, [onClose, router]);

  const handleHelp = useCallback(() => {
    onClose();
    void Linking.openURL(contactUrl);
  }, [onClose, contactUrl]);

  const handleLogout = useCallback(() => {
    onClose();
    router.push("/auth/logout");
  }, [onClose, router]);

  const handleSiteSelect = useCallback(
    (siteId: string) => {
      onSiteSelect(selectedSiteId === siteId ? null : siteId);
      setTimeout(() => {
        onClose();
      }, 100);
    },
    [onSiteSelect, selectedSiteId, onClose],
  );

  return (
    <Modal visible={modalPresented} transparent animationType="none" onRequestClose={onClose} statusBarTranslucent>
      <View style={styles.modalRoot}>
        {modalPresented && (
          <>
            <Animated.View style={[styles.backdrop, backdropAnimatedStyle]}>
              <Pressable style={styles.backdropPressable} onPress={onClose} />
            </Animated.View>

            <Animated.View
              style={[
                styles.drawer,
                drawerAnimatedStyle,
                {
                  left: -DRAWER_LEFT_OVERSHOOT_BLEED,
                  width: drawerWidth + DRAWER_LEFT_OVERSHOOT_BLEED,
                  paddingLeft: DRAWER_LEFT_OVERSHOOT_BLEED,
                  paddingTop: insets.top,
                  paddingBottom: insets.bottom,
                },
              ]}
            >
              <View px="5xl" py="3xl" flexDirection="row" alignItems="center" justifyContent="space-between">
                <Logo width={124} height={32} />
                <Pressable onPress={onClose} style={styles.closeButton}>
                  <Icon name="chevronLeft" size={20} color={theme.colors.text} />
                </Pressable>
              </View>

              <View px="5xl">
                <View height={1} bg="borderCard" />
              </View>

              <View px="5xl" py="3xl" gap="xs">
                <Text textStyle="xxs/regular" color="textMuted">
                  {tenants.length} {tenants.length === 1 ? "organisation" : "organisations"}
                </Text>
                <Pressable
                  onPress={() => {
                    onClose();
                    setTimeout(() => {
                      router.push("/settings/tenant-select");
                    }, 100);
                  }}
                >
                  <View
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    borderWidth={1}
                    borderColor="borderCard"
                    rounded={theme.spacing.xs}
                    padding="md"
                  >
                    <View flexDirection="row" alignItems="center" gap="xs">
                      <Icon name="users" size={20} color={theme.colors.textMuted} />
                      <Text textStyle="sm/regular" color="textMuted">
                        {selectedTenant ? getTenantDisplayName(selectedTenant) : "No tenant selected"}
                      </Text>
                    </View>
                    <Icon name="switch" size={20} color={theme.colors.textMuted} />
                  </View>
                </Pressable>
              </View>

              <ScrollView style={styles.siteListScroll} contentContainerStyle={styles.siteListContent}>
                {siteGroups.map((group) => (
                  <SiteCard
                    key={group.siteId}
                    group={group}
                    isSelected={selectedSiteId === group.siteId}
                    onPress={handleSiteSelect}
                  />
                ))}
              </ScrollView>

              <View px="5xl">
                <View height={1} bg="borderCard" />
              </View>

              <View px="5xl" py="3xl" gap="lg">
                <DrawerMenuItem icon="settingsOutline" label="Settings" onPress={handleSettings} />
                <DrawerMenuItem icon="help" label="Help" onPress={handleHelp} />
                <DrawerMenuItem
                  icon="logout"
                  label="Log out"
                  onPress={handleLogout}
                  iconColor={theme.colors.textMuted}
                />
              </View>
            </Animated.View>
          </>
        )}
      </View>
    </Modal>
  );
});

interface DrawerMenuItemProps {
  icon: Parameters<typeof Icon>[0]["name"];
  label: string;
  onPress: () => void;
  iconColor?: string;
}

const DrawerMenuItem = memo(({ icon, label, onPress, iconColor }: DrawerMenuItemProps) => {
  const { theme } = useUnistyles();
  const iconColorValue = iconColor ? iconColor : theme.colors.icon;

  return (
    <Pressable onPress={onPress} style={styles.menuItem}>
      <Icon name={icon} size={20} color={iconColorValue} />
      <Text textStyle="sm/regular" color={"text"}>
        {label}
      </Text>
    </Pressable>
  );
});

const styles = StyleSheet.create((theme) => ({
  modalRoot: {
    flex: 1,
  },
  backdrop: {
    ...RNStyleSheet.absoluteFillObject,
    backgroundColor: "#000",
  },
  backdropPressable: {
    flex: 1,
  },
  drawer: {
    position: "absolute",
    top: -DRAWER_BORDER_WIDTH,
    bottom: -DRAWER_BORDER_WIDTH,
    backgroundColor: theme.colors.surfaceCard,
    shadowColor: "#000",
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 20,
    borderTopRightRadius: theme.spacing.xl,
    borderBottomRightRadius: theme.spacing.xl,
    borderRightWidth: DRAWER_BORDER_WIDTH,
    borderRightColor: theme.colors.borderCard,
    borderBottomWidth: DRAWER_BORDER_WIDTH,
    borderBottomColor: theme.colors.borderCard,
    borderTopWidth: DRAWER_BORDER_WIDTH,
    borderTopColor: theme.colors.borderCard,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.secondary.default_border,
    backgroundColor: theme.colors.surfaceCard,
    alignItems: "center",
    justifyContent: "center",
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
  },
  siteListScroll: {
    flex: 1,
  },
  siteListContent: {
    paddingHorizontal: theme.spacing["5xl"],
    paddingBottom: theme.spacing["10xl"],
    gap: theme.spacing["3xl"],
    flexGrow: 1,
  },
}));
